/**
 * W-023 — Bulk ingest monitor. Stories: US-X07.
 *
 * Not a client-visible feature (docs/05 §20): it exists because the library is
 * ~100 hours and a hundred manual uploads is not a plan. The script (owned by
 * the ingest agent, run outside the browser) writes one `ingest_jobs` row per
 * file; this screen is the operator's window onto it.
 *
 * AC2 — a failure is reported without stopping the batch, so the failure report
 * below is the deliverable, not a nicety.
 *
 * TODO(US-X07): the `ingest_jobs` table is not in docs/03 §4.2 — the schema
 * there stops at the product tables. The shape used here (see types/index.ts,
 * `IngestJob`) is the working contract with the ingest script; it must land in
 * a migration before this screen does anything.
 */
import { ReloadOutlined } from '@ant-design/icons'
import { List, useTable } from '@refinedev/antd'
import { Alert, Button, Col, Row, Space, Table, Tag, Typography } from 'antd'
import { ModelInsights, StatTile } from '@/components'
import { downloadCsv } from '@/lib/csv'
import { useAdminTranslate } from '@/providers/i18n-provider'
import type { IngestJob } from '@/types'

const STATUS_TONES: Record<IngestJob['status'], string> = {
  queued: 'default',
  uploaded: 'processing',
  encoding: 'processing',
  ready: 'success',
  failed: 'error',
}

export const IngestMonitorPage = () => {
  const translate = useAdminTranslate()
  const { tableProps, tableQueryResult } = useTable<IngestJob>({
    resource: 'ingest_jobs',
    sorters: { initial: [{ field: 'updated_at', order: 'desc' }] },
    pagination: { pageSize: 50 },
    // The script writes rows continuously during a batch.
    liveMode: 'auto',
  })

  const rows = (tableProps.dataSource ?? []) as IngestJob[]
  const countBy = (status: IngestJob['status']) => rows.filter(row => row.status === status).length
  const failures = rows.filter(row => row.status === 'failed')

  return (
    <List
      title={translate('admin.pages.ingest')}
      headerProps={{
        className: 'admin-list-header',
        subTitle: translate('admin.screens.ingest.subtitle'),
      }}
      headerButtons={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => void tableQueryResult.refetch()}>
            {translate('common.refresh')}
          </Button>
          <Button
            disabled={failures.length === 0}
            onClick={() =>
              downloadCsv(translate('admin.screens.ingest.csvFileName'), failures, [
                { header: translate('admin.screens.ingest.file'), value: row => row.file_name },
                { header: translate('admin.columns.subject'), value: row => row.subject_id },
                { header: translate('admin.screens.ingest.error'), value: row => row.error },
                { header: translate('admin.columns.updatedAt'), value: row => row.updated_at },
              ])
            }
          >
            {translate('admin.screens.ingest.failureReport')}
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <ModelInsights model="ingest" />
        <Row gutter={[16, 16]}>
          <Col xs={12} md={6}>
            <StatTile
              label={translate('admin.screens.ingest.queuedTile')}
              value={countBy('queued')}
            />
          </Col>
          <Col xs={12} md={6}>
            <StatTile
              label={translate('admin.screens.ingest.uploadedTile')}
              value={countBy('uploaded')}
            />
          </Col>
          <Col xs={12} md={6}>
            <StatTile
              label={translate('admin.screens.ingest.encodingTile')}
              value={countBy('encoding')}
            />
          </Col>
          <Col xs={12} md={6}>
            <StatTile
              label={translate('admin.screens.ingest.failedTile')}
              value={countBy('failed')}
            />
          </Col>
        </Row>

        {failures.length > 0 ? (
          <Alert
            type="warning"
            showIcon
            message={translate('admin.screens.ingest.failureAlert', { count: failures.length })}
            description={translate('admin.screens.ingest.failureBody')}
          />
        ) : null}

        <Table<IngestJob>
          {...tableProps}
          rowKey="id"
          scroll={{ x: 820 }}
          sticky
          columns={[
            { title: translate('admin.screens.ingest.file'), dataIndex: 'file_name' },
            {
              title: translate('admin.columns.status'),
              dataIndex: 'status',
              render: (value: IngestJob['status']) => (
                <Tag color={STATUS_TONES[value]}>{translate(`admin.status.ingest.${value}`)}</Tag>
              ),
            },
            {
              title: translate('admin.screens.ingest.progress'),
              dataIndex: 'bytes_sent',
              render: (_: unknown, row: IngestJob) =>
                row.bytes_total
                  ? `${Math.round(((row.bytes_sent ?? 0) / row.bytes_total) * 100)} %`
                  : '—',
            },
            {
              title: translate('admin.screens.ingest.contentCreated'),
              dataIndex: 'content_id',
              render: (value: string | null) => value ?? '—',
            },
            {
              title: translate('admin.screens.ingest.error'),
              dataIndex: 'error',
              render: (value: string | null) =>
                value ? <Typography.Text type="danger">{value}</Typography.Text> : '—',
            },
          ]}
        />
      </Space>
    </List>
  )
}
